'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';

type VideoKey = 'brainrot' | 'study';

const VIDEOS: Record<VideoKey, { src: string; labelKey: string; color: 'green' | 'cyan' }> = {
  brainrot: { src: '/api/examples/brainrot-example.mp4', labelKey: 'landing.brainrotMode', color: 'green' },
  study: { src: '/api/examples/study-example.mp4', labelKey: 'landing.studyMode', color: 'cyan' },
};

export default function LandingPage() {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [activeVideo, setActiveVideo] = useState<VideoKey | null>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    if (activeVideo) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [activeVideo]);

  const getMonthlyEquivalent = (monthlyPrice: number) => {
    if (isYearly) {
      const yearlyTotal = monthlyPrice * 10;
      return Math.round(yearlyTotal / 12).toString();
    }
    return Math.round(monthlyPrice).toString();
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/15 via-black to-black" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/8 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-cyan-500/4 rounded-full blur-[128px]" />
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/5' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logo.png"
              alt="BrainEasy"
              className="w-9 h-9"
            />
            <span className="text-xl font-bold tracking-tight">BrainEasy</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#demo" className="text-sm text-gray-400 hover:text-white transition-colors">
              {t('nav.demo')}
            </a>
            <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors">
              {t('nav.features')}
            </a>
            <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">
              {t('nav.pricing')}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              href="/login"
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors px-4 py-2"
            >
              {t('nav.logIn')}
            </Link>
            <Link
              href="/signup"
              className="text-sm font-bold bg-primary text-white px-5 py-2.5 rounded-lg shadow-[0_4px_0_#CC5500] hover:brightness-110 active:shadow-none active:translate-y-1 transition-all"
            >
              {t('nav.getStartedFree')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mb-6">
            {t('landing.heroTitle1')}{' '}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-primary via-orange-400 to-yellow-500 bg-clip-text text-transparent">
                {t('landing.heroTitle2')}
              </span>
              <span className="absolute -bottom-2 left-0 right-0 h-4 bg-primary/20 blur-xl" />
            </span>
            <br />
            {t('landing.heroTitle3')} <span className="text-cyan-400">{t('landing.heroTitle4')}</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-xl mx-auto mb-10 leading-relaxed">
            {t('landing.heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/signup"
              className="group flex items-center gap-3 bg-primary text-white font-bold px-8 py-4 rounded-xl shadow-[0_6px_0_#CC5500] hover:brightness-110 active:shadow-none active:translate-y-[6px] transition-all text-lg"
            >
              {t('landing.startCreatingFree')}
              <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">
                arrow_forward
              </span>
            </Link>
            <a
              href="#demo"
              className="flex items-center gap-2 text-gray-300 hover:text-white font-medium px-6 py-4 rounded-xl border border-white/10 hover:border-white/20 transition-all"
            >
              <span className="material-symbols-outlined text-primary">play_circle</span>
              {t('landing.watchDemo')}
            </a>
          </div>
        </div>
      </section>

      {/* Demo Section — phone mockups with real videos */}
      <section id="demo" className="relative z-10 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-bold uppercase tracking-widest">{t('landing.liveDemo')}</span>
            <h2 className="text-3xl md:text-4xl font-black mt-3 mb-3">{t('landing.seeItInAction')}</h2>
            <p className="text-gray-400">{t('landing.realOutputs')}</p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16">
            {/* Brainrot Video */}
            <div className="flex flex-col items-center gap-5">
              <button
                onClick={() => setActiveVideo('brainrot')}
                className="group relative w-56 md:w-64 aspect-[9/16] bg-black rounded-[2.5rem] border-[5px] border-gray-800 shadow-2xl shadow-primary/10 overflow-hidden cursor-pointer hover:border-green-500/50 transition-colors"
                aria-label="Play Brainrot Mode demo"
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-800 rounded-b-xl z-10" />
                <video
                  src="/api/examples/brainrot-example.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-3xl ml-1">play_arrow</span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent z-10" />
              </button>
              <div className="text-center">
                <button
                  onClick={() => setActiveVideo('brainrot')}
                  className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 hover:border-green-500/50 hover:bg-green-500/20 rounded-full px-4 py-1.5 mb-2 transition-all cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 text-sm font-bold">{t('landing.brainrotMode')}</span>
                  <span className="material-symbols-outlined text-green-400 text-sm">play_circle</span>
                </button>
                <p className="text-gray-500 text-sm">{t('landing.brainrotDesc')}</p>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden md:flex flex-col items-center gap-3 text-gray-700">
              <div className="w-px h-24 bg-gradient-to-b from-transparent via-gray-700 to-transparent" />
              <span className="text-xs uppercase tracking-widest font-bold">{t('landing.vs')}</span>
              <div className="w-px h-24 bg-gradient-to-b from-transparent via-gray-700 to-transparent" />
            </div>

            {/* Study Video */}
            <div className="flex flex-col items-center gap-5">
              <button
                onClick={() => setActiveVideo('study')}
                className="group relative w-56 md:w-64 aspect-[9/16] bg-black rounded-[2.5rem] border-[5px] border-gray-800 shadow-2xl shadow-cyan-500/10 overflow-hidden cursor-pointer hover:border-cyan-500/50 transition-colors"
                aria-label="Play Study Mode demo"
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-800 rounded-b-xl z-10" />
                <video
                  src="/api/examples/study-example.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-3xl ml-1">play_arrow</span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent z-10" />
              </button>
              <div className="text-center">
                <button
                  onClick={() => setActiveVideo('study')}
                  className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 hover:border-cyan-500/50 hover:bg-cyan-500/20 rounded-full px-4 py-1.5 mb-2 transition-all cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-cyan-400 text-sm font-bold">{t('landing.studyMode')}</span>
                  <span className="material-symbols-outlined text-cyan-400 text-sm">play_circle</span>
                </button>
                <p className="text-gray-500 text-sm">{t('landing.studyDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

          {/* Modal content */}
          <div
            className="relative z-10 flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between w-full px-1">
              <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 ${
                VIDEOS[activeVideo].color === 'green'
                  ? 'bg-green-500/15 border border-green-500/30'
                  : 'bg-cyan-500/15 border border-cyan-500/30'
              }`}>
                <span className={`w-2 h-2 rounded-full animate-pulse ${
                  VIDEOS[activeVideo].color === 'green' ? 'bg-green-400' : 'bg-cyan-400'
                }`} />
                <span className={`text-sm font-bold ${
                  VIDEOS[activeVideo].color === 'green' ? 'text-green-400' : 'text-cyan-400'
                }`}>
                  {t(VIDEOS[activeVideo].labelKey)}
                </span>
              </div>
              <button
                onClick={() => setActiveVideo(null)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-all"
                aria-label="Close"
              >
                <span className="material-symbols-outlined text-white text-lg">close</span>
              </button>
            </div>

            {/* Phone frame with video player */}
            <div className="relative w-72 sm:w-80 aspect-[9/16] bg-black rounded-[2.5rem] border-[5px] border-gray-700 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-700 rounded-b-xl z-10" />
              <video
                ref={modalVideoRef}
                key={activeVideo}
                src={VIDEOS[activeVideo].src}
                controls
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            <p className="text-gray-500 text-xs">{t('landing.clickOutsideToClose')}</p>
          </div>
        </div>
      )}

      {/* Features */}
      <section id="features" className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-bold uppercase tracking-widest">{t('landing.featuresTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-black mt-3 mb-4">{t('landing.twoModesOneTool')}</h2>
            <p className="text-gray-400">{t('landing.pickYourStyle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="group relative bg-gradient-to-b from-green-500/10 to-transparent border border-green-500/20 rounded-3xl p-8 hover:border-green-500/40 transition-all overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl group-hover:bg-green-500/20 transition-colors" />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mb-5">
                  <span className="material-symbols-outlined text-green-400 text-2xl">psychology</span>
                </div>
                <h3 className="text-xl font-bold mb-3">{t('landing.brainrotGenerator')}</h3>
                <p className="text-gray-400 mb-5 leading-relaxed text-sm">
                  {t('landing.brainrotDescLong')}
                </p>
                <ul className="space-y-2.5">
                  {['viralVoiceNarration', 'autoSubtitles', 'trendingBackgrounds'].map((key) => (
                    <li key={key} className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="material-symbols-outlined text-green-400 text-sm">check</span>
                      {t(`landing.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="group relative bg-gradient-to-b from-cyan-500/10 to-transparent border border-cyan-500/20 rounded-3xl p-8 hover:border-cyan-500/40 transition-all overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl group-hover:bg-cyan-500/20 transition-colors" />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                  <span className="material-symbols-outlined text-cyan-400 text-2xl">auto_stories</span>
                </div>
                <h3 className="text-xl font-bold mb-3">{t('landing.studyHelper')}</h3>
                <p className="text-gray-400 mb-5 leading-relaxed text-sm">
                  {t('landing.studyDescLong')}
                </p>
                <ul className="space-y-2.5">
                  {['aiSummarization', 'retentionPacing', 'multipleStyles'].map((key) => (
                    <li key={key} className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="material-symbols-outlined text-cyan-400 text-sm">check</span>
                      {t(`landing.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-primary text-sm font-bold uppercase tracking-widest">{t('landing.pricingTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-black mt-3 mb-4">{t('landing.simplePricing')}</h2>
            <p className="text-gray-400 mb-8">{t('landing.choosePlan')}</p>

            <div className="inline-flex h-13 items-center justify-center rounded-2xl bg-white/5 border border-white/10 p-1.5">
              <button
                onClick={() => setIsYearly(false)}
                className={`h-10 px-6 rounded-xl text-sm font-bold transition-all ${
                  !isYearly ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {t('landing.monthly')}
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`h-10 px-6 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
                  isYearly ? 'bg-primary text-white shadow-[0_4px_0_#CC5500]' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {t('landing.yearly')}
                <span
                  className={`text-[10px] py-0.5 px-2 rounded-full uppercase tracking-wider font-black ${
                    isYearly ? 'bg-black/20 text-white' : 'bg-green-500/20 text-green-400'
                  }`}
                >
                  {t('landing.2monthsFree')}
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Free */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-7 hover:border-white/20 transition-all">
              <span className="text-gray-500 text-xs font-black uppercase tracking-widest">{t('common.free')}</span>
              <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-4xl font-black">$0</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-gray-500 text-xs mb-5">{t('landing.experienceMagic')}</p>
              <Link
                href="/signup"
                className="block w-full py-2.5 px-4 rounded-xl border border-white/10 text-center font-bold text-sm hover:bg-white/5 transition-all mb-5"
              >
                {t('landing.getStarted')}
              </Link>
              <div className="flex items-start gap-3 text-sm font-bold">
                <span className="material-symbols-outlined text-primary text-lg">token</span>
                {t('landing.creditsPerMonth')}
              </div>
            </div>

            {/* Tryout */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-7 hover:border-primary/30 hover:-translate-y-1 transition-all">
              <span className="text-gray-500 text-xs font-black uppercase tracking-widest">{t('common.tryout')}</span>
              <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-4xl font-black">$9</span>
                <span className="text-gray-500 text-sm">/ 7 days</span>
              </div>
              <p className="text-gray-500 text-xs mb-5">{t('landing.7dayTrialThenStarter')}</p>
              <Link
                href="/signup?plan=tryout"
                className="block w-full py-2.5 px-4 rounded-xl border-2 border-primary text-primary text-center font-bold text-sm hover:bg-primary hover:text-white transition-all mb-5"
              >
                {t('landing.startTrial')}
              </Link>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm font-bold">
                  <span className="material-symbols-outlined text-primary text-lg">token</span>
                  {t('landing.creditsTryout')}
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-300">
                  <span className="material-symbols-outlined text-green-400 text-lg">check_circle</span>
                  {t('landing.noWatermarks')}
                </li>
              </ul>
            </div>

            {/* Starter */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-7 hover:border-primary/30 hover:-translate-y-1 transition-all">
              <span className="text-gray-500 text-xs font-black uppercase tracking-widest">{t('common.starter')}</span>
              <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-4xl font-black">${getMonthlyEquivalent(19)}</span>
                <span className="text-gray-500 text-sm">/mo</span>
              </div>
              {isYearly && <p className="text-green-400 text-[10px] font-bold uppercase tracking-widest mb-1">Billed as $190/yr</p>}
              <p className="text-gray-500 text-xs mb-5">{t('landing.forStudentsCreators')}</p>
              <Link
                href="/signup?plan=starter"
                className="block w-full py-2.5 px-4 rounded-xl border-2 border-primary text-primary text-center font-bold text-sm hover:bg-primary hover:text-white transition-all mb-5"
              >
                {t('landing.selectPlan')}
              </Link>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm font-bold">
                  <span className="material-symbols-outlined text-primary text-lg">token</span>
                  {t('landing.creditsStarter')}
                </li>
                {['backgroundVideos', 'backgroundMusic', 'noWatermarks'].map((key) => (
                  <li key={key} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="material-symbols-outlined text-green-400 text-lg">check_circle</span>
                    {t(`landing.${key}`)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro */}
            <div className="relative bg-gradient-to-b from-primary/10 to-transparent border-2 border-primary rounded-3xl p-7 hover:scale-[1.02] transition-all shadow-xl shadow-primary/10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                {t('landing.bestValue')}
              </div>
              <span className="text-primary text-xs font-black uppercase tracking-widest">{t('common.pro')}</span>
              <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-5xl font-black">${getMonthlyEquivalent(39)}</span>
                <span className="text-gray-500 text-sm">/mo</span>
              </div>
              {isYearly && <p className="text-green-400 text-[10px] font-bold uppercase tracking-widest mb-1">Billed as $390/yr</p>}
              <p className="text-gray-500 text-xs mb-5">{t('landing.scalePersonalBrand')}</p>
              <Link
                href="/signup?plan=pro"
                className="block w-full py-3 px-4 rounded-2xl bg-primary text-white text-center font-black text-sm shadow-[0_6px_0_#CC5500] hover:brightness-110 active:shadow-none active:translate-y-1 transition-all mb-5"
              >
                {t('landing.goViralNow')}
              </Link>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm font-black">
                  <span className="material-symbols-outlined text-primary text-lg">token</span>
                  {t('landing.creditsPro')}
                </li>
                <li className="flex items-start gap-3 text-sm text-primary font-bold">
                  <span className="material-symbols-outlined text-primary text-lg">star</span>
                  {t('landing.allInStarter')}
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-300">
                  <span className="material-symbols-outlined text-green-400 text-lg">check_circle</span>
                  {t('landing.aiGeneratedContent')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="relative bg-gradient-to-r from-primary/20 via-primary/10 to-cyan-500/10 rounded-3xl p-12 border border-primary/20 overflow-hidden text-center">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwaDQwdjQwSDB6IiBmaWxsPSJub25lIi8+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvc3ZnPg==')] opacity-50" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-black mb-4">{t('landing.readyToGoViral')}</h2>
              <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                {t('landing.joinCreators')}
              </p>
              <Link
                href="/signup"
                className="inline-flex items-center gap-3 bg-white text-black font-bold px-8 py-4 rounded-xl hover:bg-gray-100 transition-all text-lg group"
              >
                {t('landing.startCreatingFree')}
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">
                  arrow_forward
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logo.png"
              alt="BrainEasy"
              className="w-7 h-7"
            />
            <span className="text-base font-bold">BrainEasy</span>
          </div>
          <div className="flex items-center gap-8 text-sm text-gray-500">
            <Link href="/terms" className="hover:text-white transition-colors">{t('nav.terms')}</Link>
            <Link href="/privacy" className="hover:text-white transition-colors">{t('nav.privacy')}</Link>
            <a href="mailto:support@braineasy.app" className="hover:text-white transition-colors">{t('nav.contact')}</a>
          </div>
          <p className="text-sm text-gray-600">{t('landing.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
